import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type TimeEntryOutput,
  type CreateTimeEntryInput,
  type UpdateTimeEntryInput,
  type UserOutput,
  type EngagementOutput,
  type ComplianceItemOutput,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { downloadFromApi } from '../lib/download.js';
import { SortableTh, TablePager, useTableControls } from '../components/TableControls.js';
import { FilterBar, EmptyFilterRow, type ActiveFilter } from '../components/FilterBar.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { EditIcon, DeleteIcon } from '../components/ActionIcons.js';
import { Clock } from 'lucide-react';
import { fmtDate } from '../lib/date.js';
import { useUserPreference } from '../lib/user-preferences.js';

function formatTimer(ms: number) {
  const seconds = Math.floor(ms / 1000);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function timerElapsedMs(entry: { startedAt: string; pausedAt: string | null; pausedSeconds: number }) {
  const endMs = entry.pausedAt ? new Date(entry.pausedAt).getTime() : Date.now();
  return Math.max(0, endMs - new Date(entry.startedAt).getTime() - entry.pausedSeconds * 1000);
}

function RunningBadge({ entry }: { entry: { startedAt: string; pausedAt: string | null; pausedSeconds: number } }) {
  const [ms, setMs] = useState(timerElapsedMs(entry));
  useEffect(() => {
    const id = setInterval(() => setMs(timerElapsedMs(entry)), 1000);
    return () => clearInterval(id);
  }, [entry.startedAt, entry.pausedAt, entry.pausedSeconds]);
  if (entry.pausedAt) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">Paused {formatTimer(ms)}</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Running {formatTimer(ms)}</span>;
}


type ModalMode = { kind: 'new' } | { kind: 'edit'; entry: TimeEntryOutput } | null;

export function TimeEntriesPage() {
  const { api, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useUserPreference('time.filters', {
    userId: '',
    engagementId: '',
    complianceClientId: '',
    complianceItemId: '',
    billable: '',
  }, {
    validate: (value) => !!value && typeof value === 'object'
      && ['userId', 'engagementId', 'complianceClientId', 'complianceItemId', 'billable']
        .every((key) => typeof (value as Record<string, unknown>)[key] === 'string'),
  });
  const [modal, setModal] = useState<ModalMode>(null);
  const [deleting, setDeleting] = useState<TimeEntryOutput | null>(null);

  const { canDo } = useCapabilities();
  const isStaff = !canDo('time.view_all');
  const canLog = canDo('time.log');
  const canChange = (entry: TimeEntryOutput) => canLog && !entry.invoicedAt && (entry.userId === currentUser?.id || canDo('time.edit_others'));
  const { sortBy, sortOrder, cursor, pageIndex, toggleSort, goNext, goPrev } = useTableControls('date', 'desc', [filters]);

  function exportTimeEntries() {
    const p = new URLSearchParams();
    if (!isStaff && filters.userId) p.set('userId', filters.userId);
    if (filters.engagementId) p.set('engagementId', filters.engagementId);
    if (filters.complianceClientId) p.set('complianceClientId', filters.complianceClientId);
    if (filters.complianceItemId) p.set('complianceItemId', filters.complianceItemId);
    if (filters.billable) p.set('billable', filters.billable);
    downloadFromApi(`/v1/time-entries/export?${p.toString()}`, 'time-entries.csv')
      .catch((e) => alert(e instanceof Error ? e.message : 'Export failed'));
  }

  const list = useQuery({
    queryKey: ['time-entries', filters, sortBy, sortOrder, cursor],
    enabled: api !== null,
    refetchInterval: 10000,
    queryFn: async () => {
      const p = new URLSearchParams();
      p.set('limit', '100');
      p.set('sortBy', sortBy);
      p.set('sortOrder', sortOrder);
      if (cursor) p.set('cursor', cursor);
      if (!isStaff && filters.userId) p.set('userId', filters.userId);
      if (filters.engagementId) p.set('engagementId', filters.engagementId);
      if (filters.complianceClientId) p.set('complianceClientId', filters.complianceClientId);
      if (filters.complianceItemId) p.set('complianceItemId', filters.complianceItemId);
      if (filters.billable) p.set('billable', filters.billable);

      const res = await fetch(`/v1/time-entries?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: TimeEntryOutput[]; total: number; nextCursor: string | null } };
      return body.data;
    },
  });

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    enabled: api !== null,
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  const engagements = useQuery({
    queryKey: ['engagements', { limit: 100 }],
    enabled: api !== null,
    queryFn: async () => {
      const res = await fetch('/v1/engagements?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: EngagementOutput[] } };
      return body.data.items;
    },
  });

  const complianceItems = useQuery({
    queryKey: ['compliance-items', { limit: 100, clientId: filters.complianceClientId }],
    enabled: api !== null,
    queryFn: async () => {
      const p = new URLSearchParams();
      p.set('limit', '100');
      if (filters.complianceClientId) p.set('clientId', filters.complianceClientId);
      const res = await fetch(`/v1/compliance/items?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: ComplianceItemOutput[] } };
      // Completed items are locked — exclude them as time-log targets.
      return body.data.items.filter((i) => i.status !== 'Completed');
    },
  });

  const complianceClients = useQuery({
    queryKey: ['compliance-clients', { limit: 100, all: false }],
    enabled: api !== null,
    queryFn: async () => {
      const res = await fetch('/v1/compliance/clients?limit=100&all=false&clientStatus=ACTIVE', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: { id: string; name: string; company: string | null; engagementId: string | null }[] } };
      return body.data.items;
    },
  });

  const create = useMutation({
    mutationFn: async (input: CreateTimeEntryInput) => {
      const res = await fetch('/v1/time-entries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({
          ...input,
          date: input.date.length === 10 ? `${input.date}T00:00:00.000Z` : input.date,
        }),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      setModal(null);
    },
  });

  const startTimer = useMutation({
    mutationFn: async (input: { engagementId?: string | null; complianceItemId?: string | null; complianceClientId?: string | null; description?: string | null; billable?: boolean }) => {
      const res = await fetch('/v1/time-entries/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json() as Promise<{ ok: true; data: TimeEntryOutput }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time-entries'] }),
  });

  const stopTimer = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/time-entries/${id}/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw await httpError(res);
      return res.json() as Promise<{ ok: true; data: TimeEntryOutput }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time-entries'] }),
  });

  const pauseTimer = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/time-entries/${id}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw await httpError(res);
      return res.json() as Promise<{ ok: true; data: TimeEntryOutput }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time-entries'] }),
  });

  const resumeTimer = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/time-entries/${id}/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw await httpError(res);
      return res.json() as Promise<{ ok: true; data: TimeEntryOutput }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['time-entries'] }),
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateTimeEntryInput }) => {
      const res = await fetch(`/v1/time-entries/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({
          ...input,
          ...(input.date && { date: input.date.length === 10 ? `${input.date}T00:00:00.000Z` : input.date }),
        }),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      setModal(null);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/time-entries/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-entries'] });
      setDeleting(null);
    },
  });

  function formatHours(h: number) {
  const whole = Math.floor(h);
  const mins = Math.round((h - whole) * 60);
  return `${whole}:${mins.toString().padStart(2, '0')}`;
}

function userName(uid: string | null) {
    if (!uid) return '—';
    const u = users.data?.find((x) => x.id === uid);
    return u ? u.displayName : uid.slice(0, 8);
  }

  function engagementName(eid: string | null, summary?: TimeEntryOutput['engagement']) {
    if (!eid) return '—';
    if (summary) {
      const client = summary.client.company ?? summary.client.name;
      return summary.title ? `${client} — ${summary.title}` : client;
    }
    const e = engagements.data?.find((x) => x.id === eid);
    if (!e) return eid.slice(0, 8);
    const client = e.clientName || e.clientCompany || '—';
    return e.title ? `${client} — ${e.title}` : client;
  }

  function complianceClientName(cid: string | null, summary?: TimeEntryOutput['complianceClient']) {
    if (!cid) return '—';
    const c = summary ?? complianceClients.data?.find((x) => x.id === cid);
    return c ? c.name : cid.slice(0, 8);
  }

  function itemName(iid: string | null, summary?: TimeEntryOutput['complianceItem']) {
    if (!iid) return '—';
    const i = summary ?? complianceItems.data?.find((x) => x.id === iid);
    return i ? `${i.serviceType} (${i.jurisdiction ?? '—'})` : iid.slice(0, 8);
  }

  const activeFilters: ActiveFilter[] = [
    ...(filters.userId ? [{ key: 'userId', label: `User: ${userName(filters.userId)}` }] : []),
    ...(filters.engagementId ? [{ key: 'engagementId', label: `Engagement: ${engagementName(filters.engagementId)}` }] : []),
    ...(filters.complianceClientId ? [{ key: 'complianceClientId', label: `Client: ${complianceClientName(filters.complianceClientId)}` }] : []),
    ...(filters.complianceItemId ? [{ key: 'complianceItemId', label: `Item: ${itemName(filters.complianceItemId)}` }] : []),
    ...(filters.billable ? [{ key: 'billable', label: filters.billable === 'true' ? 'Billable only' : 'Non-billable only' }] : []),
  ];

  function removeFilter(key: string) {
    setFilters((f) => ({ ...f, [key]: '', ...(key === 'complianceClientId' ? { complianceItemId: '' } : {}) }));
  }

  function clearAllFilters() {
    setFilters({ userId: '', engagementId: '', complianceClientId: '', complianceItemId: '', billable: '' });
  }

  const totalHours = list.data?.items.reduce((sum, t) => sum + t.hours, 0) ?? 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Time tracking</h1>
          <p className="text-sm text-muted">Log, edit and review hours across engagements and compliance items.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportTimeEntries} className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg">Export</button>
          <button onClick={() => setModal({ kind: 'new' })} disabled={!canLog} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">+ Log time</button>
        </div>
      </header>

      <FilterBar activeFilters={activeFilters} onRemoveFilter={removeFilter} onClearAll={clearAllFilters}>
        {!isStaff && (
          <SearchableSelect
            value={filters.userId}
            onChange={(v) => setFilters((f) => ({ ...f, userId: v }))}
            placeholder="All users"
            options={(users.data ?? []).filter((u) => u.status !== "DISABLED").map((u) => ({ value: u.id, label: u.displayName }))}
            className="w-full bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white"
            wrapperClassName="w-full sm:w-52"
          />
        )}
        <SearchableSelect
          value={filters.engagementId}
          onChange={(v) => setFilters((f) => ({ ...f, engagementId: v }))}
          placeholder="All engagements"
          options={(engagements.data ?? []).map((e) => ({ value: e.id, label: `${e.clientName || e.clientCompany || '—'} — ${e.title ?? e.id.slice(0, 8)}` }))}
          className="w-full bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white"
          wrapperClassName="w-full sm:w-64"
        />
        <SearchableSelect
          value={filters.complianceClientId}
          onChange={(v) => setFilters((f) => ({ ...f, complianceClientId: v, complianceItemId: '' }))}
          placeholder="All compliance clients"
          options={(complianceClients.data ?? []).map((c) => ({ value: c.id, label: `${c.name}${c.company ? ` (${c.company})` : ''}` }))}
          className="w-full bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white"
          wrapperClassName="w-full sm:w-52"
        />
        <select
          value={filters.complianceItemId}
          onChange={(e) => setFilters((f) => ({ ...f, complianceItemId: e.target.value }))}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white"
        >
          <option value="">All compliance items</option>
          {complianceItems.data?.map((i) => <option key={i.id} value={i.id}>{i.serviceType} ({i.jurisdiction ?? '—'})</option>)}
        </select>
        <select
          value={filters.billable}
          onChange={(e) => setFilters((f) => ({ ...f, billable: e.target.value }))}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white"
        >
          <option value="">Billable & non-billable</option>
          <option value="true">Billable only</option>
          <option value="false">Non-billable only</option>
        </select>
      </FilterBar>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">{list.data?.total ?? 0} entries · {formatHours(totalHours)} total</p>
      </div>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[896px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <SortableTh label="Date" sortKey="date" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="User" sortKey="user" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <th className="px-4 py-3 font-medium">Engagement</th>
              <th className="px-4 py-3 font-medium">Compliance client</th>
              <th className="px-4 py-3 font-medium">Compliance item</th>
              <th className="px-4 py-3 font-medium">Description</th>
              <SortableTh label="Hours" sortKey="hours" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Billable" sortKey="billable" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <TableSkeletonRows colSpan={9} />
            )}
            {list.error && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>
            )}
            {!list.isLoading && !list.error && list.data?.items.length === 0 && (
              activeFilters.length > 0
                ? <EmptyFilterRow colSpan={9} onClearAll={clearAllFilters} />
                : <tr><td colSpan={9}><EmptyState icon={Clock} title="No time entries yet." /></td></tr>
            )}
            {list.data?.items.map((t) => (
              <tr key={t.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-3 text-white">{fmtDate(t.date)}</td>
                <td className="px-4 py-3 text-white">{userName(t.userId)}</td>
                <td className="px-4 py-3 text-white">
                  {t.engagementId ? engagementName(t.engagementId, t.engagement) : '—'}
                </td>
                <td className="px-4 py-3 text-white">
                  {t.complianceClientId ? complianceClientName(t.complianceClientId, t.complianceClient) : '—'}
                </td>
                <td className="px-4 py-3 text-white">
                  {t.complianceItemId ? itemName(t.complianceItemId, t.complianceItem) : '—'}
                </td>
                <td className="px-4 py-3 text-white">{t.description ?? '—'}</td>
                <td className="px-4 py-3 text-white">
                  <div className="flex items-center gap-2">
                    {formatHours(t.hours)}
                    {t.running && t.startedAt && <RunningBadge entry={{ startedAt: t.startedAt, pausedAt: t.pausedAt, pausedSeconds: t.pausedSeconds }} />}
                  </div>
                </td>
                <td className="px-4 py-3 text-white">{t.billable ? 'Yes' : 'No'}</td>
                <td className="px-4 py-3 text-right">
                  {canChange(t) && t.userId === currentUser?.id && t.running && !t.pausedAt && (
                    <button onClick={() => pauseTimer.mutate(t.id)} disabled={pauseTimer.isPending} className="text-xs bg-amber-600 hover:bg-amber-500 text-oncolor px-2 py-1 rounded mr-2">Pause</button>
                  )}
                  {canChange(t) && t.userId === currentUser?.id && t.running && t.pausedAt && (
                    <button onClick={() => resumeTimer.mutate(t.id)} disabled={resumeTimer.isPending} className="text-xs bg-emerald-600 hover:bg-emerald-500 text-oncolor px-2 py-1 rounded mr-2">Resume</button>
                  )}
                  {canChange(t) && t.userId === currentUser?.id && t.running && (
                    <button onClick={() => stopTimer.mutate(t.id)} disabled={stopTimer.isPending} className="text-xs bg-rose-600 hover:bg-rose-500 text-oncolor px-2 py-1 rounded mr-2">Stop</button>
                  )}
                  {canChange(t) && !t.running && (
                    <div className="inline-flex items-center gap-1.5">
                      <EditIcon onClick={() => setModal({ kind: 'edit', entry: t })} label="Edit time entry" />
                      <DeleteIcon onClick={() => setDeleting(t)} label="Delete time entry" />
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager
          pageIndex={pageIndex}
          count={list.data?.items.length ?? 0}
          limit={100}
          total={list.data?.total}
          hasNext={Boolean(list.data?.nextCursor)}
          onPrev={goPrev}
          onNext={() => goNext(list.data?.nextCursor)}
        />
      </div>

      {modal && (
        <TimeEntryModal
          mode={modal}
          engagements={engagements.data ?? []}
          complianceClients={complianceClients.data ?? []}
          complianceItems={complianceItems.data ?? []}
          onClose={() => setModal(null)}
          onSubmit={() => {}}
          isPending={create.isPending || update.isPending || startTimer.isPending || stopTimer.isPending || pauseTimer.isPending || resumeTimer.isPending}
          error={(create.error?.message ?? startTimer.error?.message ?? stopTimer.error?.message ?? pauseTimer.error?.message ?? resumeTimer.error?.message ?? update.error?.message) || null}
          startTimer={startTimer}
          stopTimer={stopTimer}
          pauseTimer={pauseTimer}
          resumeTimer={resumeTimer}
          update={update}
        />
      )}

      {deleting && (
        <DeleteModal
          entry={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting.id)}
          isPending={remove.isPending}
          error={remove.error?.message ?? null}
        />
      )}
    </div>
  );
}

function TimeEntryModal({
  mode,
  engagements,
  complianceClients,
  complianceItems,
  onClose,
  onSubmit,
  isPending,
  error,
  startTimer,
  stopTimer,
  pauseTimer,
  resumeTimer,
  update,
}: {
  mode: { kind: 'new' } | { kind: 'edit'; entry: TimeEntryOutput };
  engagements: EngagementOutput[];
  complianceClients: { id: string; name: string; company: string | null; engagementId: string | null }[];
  complianceItems: ComplianceItemOutput[];
  onClose: () => void;
  onSubmit: (input: CreateTimeEntryInput | UpdateTimeEntryInput) => void;
  isPending: boolean;
  error: string | null;
  startTimer: {
    mutate: (
      input: { engagementId?: string | null; complianceItemId?: string | null; complianceClientId?: string | null; description?: string | null; billable?: boolean },
      opts?: { onSuccess?: (res: { data: TimeEntryOutput }) => void },
    ) => void;
    isPending: boolean;
    data?: { data: TimeEntryOutput } | null;
  };
  stopTimer: { mutate: (id: string, opts?: { onSuccess?: () => void }) => void; isPending: boolean };
  pauseTimer: { mutate: (id: string, opts?: { onSuccess?: (res: { data: TimeEntryOutput }) => void }) => void; isPending: boolean };
  resumeTimer: { mutate: (id: string, opts?: { onSuccess?: (res: { data: TimeEntryOutput }) => void }) => void; isPending: boolean };
  update: { mutate: (arg: { id: string; input: UpdateTimeEntryInput }, opts?: { onSuccess?: () => void }) => void; isPending: boolean };
}) {
  const isEdit = mode.kind === 'edit';
  const entry = isEdit ? mode.entry : null;
  const [form, setForm] = useState({
    engagementId: entry?.engagementId ?? null,
    complianceItemId: entry?.complianceItemId ?? null,
    complianceClientId: entry?.complianceClientId ?? null,
    description: entry?.description ?? null,
    billable: entry?.billable ?? true,
  });
  const [active, setActive] = useState<TimeEntryOutput | null>(entry && entry.running ? entry : null);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!active || !active.running || !active.startedAt) return;
    const tick = () => setElapsedMs(timerElapsedMs({ startedAt: active.startedAt!, pausedAt: active.pausedAt, pausedSeconds: active.pausedSeconds }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active?.id, active?.startedAt, active?.running, active?.pausedAt, active?.pausedSeconds]);

  function updateForm<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const selectedTarget = form.engagementId || form.complianceItemId || form.complianceClientId;
  const canStart = !active && Boolean(selectedTarget);

  function onStart() {
    if (!selectedTarget) return;
    startTimer.mutate({
      engagementId: form.engagementId,
      complianceItemId: form.complianceItemId,
      complianceClientId: form.complianceClientId,
      description: form.description,
      billable: form.billable,
    }, {
      onSuccess: (res) => {
        setActive(res.data);
      },
    });
  }

  function onSave() {
    if (active) {
      update.mutate({ id: active.id, input: { description: form.description, billable: form.billable } }, {
        onSuccess: () => onClose(),
      });
    } else if (entry) {
      update.mutate({ id: entry.id, input: { description: form.description, billable: form.billable } }, {
        onSuccess: () => onClose(),
      });
    } else {
      onClose();
    }
  }

  function onStop() {
    const id = active?.id ?? entry?.id;
    if (!id) return;
    stopTimer.mutate(id, { onSuccess: () => onClose() });
  }

  function onPause() {
    const id = active?.id ?? entry?.id;
    if (!id) return;
    pauseTimer.mutate(id, { onSuccess: (res) => setActive(res.data) });
  }

  function onResume() {
    const id = active?.id ?? entry?.id;
    if (!id) return;
    resumeTimer.mutate(id, { onSuccess: (res) => setActive(res.data) });
  }

  return (
    <ModalShell title={isEdit ? 'Edit time entry' : 'Log time'} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-muted mb-1">Engagement</label>
          <SearchableSelect
            value={form.engagementId ?? ''}
            disabled={Boolean(active) || Boolean(entry && entry.running)}
            onChange={(v) => updateForm('engagementId', v || null)}
            placeholder="None"
            options={engagements.map((e) => ({ value: e.id, label: e.title ?? e.id.slice(0, 8) }))}
          />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Compliance client</label>
          <SearchableSelect
            value={form.complianceClientId ?? ''}
            disabled={Boolean(active) || Boolean(entry && entry.running)}
            onChange={(v) => {
              updateForm('complianceClientId', v || null);
              updateForm('complianceItemId', null);
            }}
            placeholder="None"
            options={complianceClients.map((c) => ({ value: c.id, label: `${c.name}${c.company ? ` (${c.company})` : ''}` }))}
          />
        </div>
        <ComplianceItemSelect
          complianceClientId={form.complianceClientId}
          value={form.complianceItemId}
          onChange={(v) => updateForm('complianceItemId', v)}
          disabled={Boolean(active) || Boolean(entry && entry.running)}
        />
        <div>
          <label className="block text-xs text-muted mb-1">Description</label>
          <textarea value={form.description ?? ''} onChange={(e) => updateForm('description', e.target.value || null)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" rows={3} />
        </div>
        <label className="flex items-center gap-2 text-sm text-white">
          <input type="checkbox" checked={form.billable} onChange={(e) => updateForm('billable', e.target.checked)} />
          Billable
        </label>

        {(active || (entry && entry.running)) && (
          <div className="flex items-center gap-3 bg-bg-card border border-border rounded-lg px-3 py-2">
            <span className="text-xs text-muted">Timer</span>
            <span className="text-lg text-white font-mono">{formatTimer(elapsedMs)}</span>
            {active?.pausedAt
              ? <span className="text-xs text-amber-400 ml-auto">❚❚ Paused</span>
              : <span className="text-xs text-emerald-400 ml-auto animate-pulse">● Running</span>}
          </div>
        )}

        {error && <p className="text-xs text-rose-400">{error}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          {canStart && (
            <button
              type="button"
              onClick={onStart}
              disabled={startTimer.isPending}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              {startTimer.isPending ? 'Starting…' : 'Start'}
            </button>
          )}
          {(active || entry) && (
            <button
              type="button"
              onClick={onSave}
              disabled={update.isPending || startTimer.isPending}
              className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          )}
          {(active || (entry && entry.running)) && !active?.pausedAt && (
            <button
              type="button"
              onClick={onPause}
              disabled={pauseTimer.isPending}
              className="bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              {pauseTimer.isPending ? 'Pausing…' : 'Pause'}
            </button>
          )}
          {(active || (entry && entry.running)) && active?.pausedAt && (
            <button
              type="button"
              onClick={onResume}
              disabled={resumeTimer.isPending}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              {resumeTimer.isPending ? 'Resuming…' : 'Resume'}
            </button>
          )}
          {(active || (entry && entry.running)) && (
            <button
              type="button"
              onClick={onStop}
              disabled={stopTimer.isPending}
              className="bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              {stopTimer.isPending ? 'Stopping…' : 'Stop'}
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function DeleteModal({ entry, onClose, onConfirm, isPending, error }: { entry: TimeEntryOutput; onClose: () => void; onConfirm: () => void; isPending: boolean; error: string | null }) {
  return (
    <ModalShell title="Delete time entry" onClose={onClose}>
      <p className="text-sm text-white">Delete {entry.hours.toFixed(2)}h logged on {fmtDate(entry.date)}?</p>
      {error && <p className="text-xs text-rose-400 mt-2">{error}</p>}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
        <button type="button" onClick={onConfirm} disabled={isPending} className="bg-rose-500 hover:bg-rose-400 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg">{isPending ? 'Deleting…' : 'Delete'}</button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-xl mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ComplianceItemSelect({ complianceClientId, value, onChange, disabled }: { complianceClientId: string | null; value: string | null; onChange: (v: string | null) => void; disabled: boolean }) {
  const items = useQuery({
    queryKey: ['compliance-items', { limit: 100, clientId: complianceClientId ?? '' }],
    enabled: Boolean(complianceClientId),
    queryFn: async () => {
      const p = new URLSearchParams();
      p.set('limit', '100');
      p.set('clientId', complianceClientId!);
      const res = await fetch(`/v1/compliance/items?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: ComplianceItemOutput[] } };
      // Completed items are locked — exclude them as time-log targets.
      return body.data.items.filter((i) => i.status !== 'Completed');
    },
  });

  return (
    <div>
      <label className="block text-xs text-muted mb-1">Compliance item</label>
      <select
        value={value ?? ''}
        disabled={disabled || !complianceClientId}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50"
      >
        <option value="">{complianceClientId ? (items.isLoading ? 'Loading…' : items.data?.length ? 'Select item' : 'No items') : 'Select compliance client first'}</option>
        {items.data?.map((i) => <option key={i.id} value={i.id}>{i.serviceType} ({i.jurisdiction ?? '—'})</option>)}
      </select>
      {items.error && <p className="text-xs text-rose-400 mt-1">{(items.error as Error).message}</p>}
    </div>
  );
}

